<template lang="pug">
div
  slot(name="logo")

  Maintenance(v-if="maintenance")

  template(v-else-if="view === LVIEW.EMAIL_CONFIRM")
    ConfirmEmail(@click="openLogin")
  template(v-else-if="view === LVIEW.DEFAULT")

    Loading(v-if="hidden" color="#ffffff")

    form#content-wrapper(:class="{'hidden': hidden}" @submit.prevent="signIn")
      div#title
        h1#magic-title This is where
          br
          | the magic happens
        h5 Let's get creative.
          br
          | Log in and start building.

      div#form
        OAuthSignIn

        Divider.fs-14.color-white(type="solid" text="or" style="--border-color: hsla(0, 0%, 100%, .15)")

        div.flex.flex-direction-column.row-gap-4
          label E-mail
          Input(
            id="email"
            variant="email"
            placeholder="someone@domain.com"
            autocomplete="email"
            :validate="false"
            :medium="true"
            v-model="email"
          )

        div.flex.flex-direction-column.row-gap-4
          label Password
          Input(
            id="password"
            variant="password"
            placeholder="password"
            autocomplete="password"
            :validate="false"
            :medium="true"
            v-model="password"
          )

        Button(
          v-if="email_verify_url"
          label="Resend verification email"
          type="login-verify"
          :outline="true"
          :medium="true"
          :block="true"
          :loading="loading_verifying"
          :disabled="loading"
          @click="verifyEmail"
        )

        div.text-center(v-if="form_success && is_april_fools && $parent.music != null")
          small.cursor-pointer(@click="redirect") Refresh or click here to skip the wait

        Button(
          :label="login_button_label"
          :type="form_success ? 'success' : 'login-primary'"
          :block="true"
          :medium="true"
          :disabled="loading || loading_verifying || form_success"
          :loading="loading && !email_verify_url"
          :style="{opacity: 1}"
          :submit="!form_success"
        )

        GoogleCaptcha(v-if="require_captcha" ref="captcha")

        Button.fs-14(
          label="Don't have an account?"
          type="login-secondary"
          :block="true"
          :medium="true"
          @click="$parent.open('register')"
        )

        Button.fs-14(
          label="Forgot password?"
          type="link-light"
          :block="true"
          :text="true"
          @click="$parent.open('reset')"
        )

</template>

<script>

import LoginService from '@login/LoginService';

import GoogleCaptcha from '@login/Components/GoogleCaptcha.vue';
import Maintenance from '@login/Components/Maintenance.vue';
import ConfirmEmail from '@login/Components/ConfirmEmail.vue';
import OAuthSignIn from '@master/UI/OAuthSignIn.vue';
import Input from '@master/UI/Input/Input.vue';
import Button from '@master/UI/Buttons/Button.vue';
import Divider from '@master/UI/Divider/Divider.vue';
import Loading from '@master/UI/Loading.vue';

import { VIEW } from '@master/constants';

const LVIEW = {
  DEFAULT: 'login',
  EMAIL_CONFIRM: 'emailconfirm'
};

export default {
  name: 'Login',

  components: {
    GoogleCaptcha,
    Maintenance,
    ConfirmEmail,
    Input,
    Button,
    OAuthSignIn,
    Divider,
    Loading
  },

  computed: {
    is_april_fools() {
      return window._april_fools_elevator_login != null;
    }
  },

  data() {
    return {
      LVIEW,

      maintenance: false,
      view: null,
      success: false,
      error: false,
      email: '',
      password: '',
      client_token: null,
      user: null,
      hidden: true,
      redirect_url: '/home',
      require_captcha: false,
      email_verify_url: null,
      loading: false,
      loading_verifying: false,
      form_success: false,
      wave: true,
      login_button_label: 'Log me in!'
    };
  },

  created() {
    if (this.$route.query.confirm != null) {
      this.view = LVIEW.EMAIL_CONFIRM;
    } else {
      this.makeCookieRequest()
        .then(status => {
          if (status) {
            if (this.handleRedirectRef()) {
              return;
            }
            // supress auto redirect error
            // happens when cookie request is a success but /me request is not
            // that case would produce inf. loop that will throw a route guard error
            return this.$router.push({ name: VIEW.CAMPAIGNS })
              .catch(_ => { /** supress errors */ });
          }
        })
        .finally(() => {
          this.view = LVIEW.DEFAULT;
        });
    }

    if (window._christmas_8bit) {
      this.wave = false;
    }

    if (process.env.NODE_ENV === 'development') {
      // autofill random email from registration
      this.email = LoginService.get('email') ?? '';
    }

    if (this.maintenance) {
      this.handleElevatorMusic();
    }
  },

  methods: {
    handleRedirectRef() {
      if (this.$route.query.ref == null) {
        return false;
      }

      this.redirect_url = decodeURIComponent(this.$route.query.ref);
      // if the redirect ref is https://something.com (full url) reidrect to that instead of making any other checks
      if (this.redirect_url.match(/https:\/\//g)) {
        this.redirect();

        // return true so any code that called this could stop
        return true;
      }

      return false;
    },

    signIn() {
      if (this.loading || this.form_success) return;

      this.loading = true;

      this.makeCookieRequest()
        .then(() => {

          // if not logged in get token and do login
          // In case captcha is needed - let's validate it
          if (this.require_captcha && this.$refs.captcha.getToken() == null) {
            this.$refs.captcha.showError();
            this.loading = false;
            return false;
          }

          const obj = {
            email: this.email,
            password: this.password,
          };

          if (this.require_captcha) {
            obj._captcha = this.$refs.captcha.getToken();
          }

          this.$http.post('user/login', obj, {notification: 'danger'})
            .then(async _ => {
              this.form_success = true;

              await this.$nextTick();

              if (this.handleRedirectRef()) {
                return;
              }

              if (process.env.NODE_ENV === 'development') {
                // add dev env hash route
                this.redirect_url = '/#' + this.redirect_url;
              }

              // replace all double slashes // with /
              this.redirect_url = this.redirect_url.replace(/\/\//g, '/');

              let timer = null;
              let wait_before_redirect = 0;

              if (this.is_april_fools) {
                const extra = this.handleElevatorMusic();
                timer = extra.timer;
                wait_before_redirect = extra.wait_before_redirect;
              }

              setTimeout(() => {
                window.clearInterval(timer);
                this.redirect();
              }, wait_before_redirect);
            })
            .catch(error => {
              // If captcha already in place - let's reset it
              if (this.require_captcha) {
                this.$refs.captcha.reset();
              }

              // API will tell us - what it needs
              if (error?.response?.result) {
                this.require_captcha = error.response.result.captcha_required ?? false;
                this.email_verify_url = error.response.result.verify_url ?? null;
              }
            })
            .finally(() => { this.loading = false; });
        })
        .catch(() => {
          this.loading = false;
        });
    },

    makeCookieRequest() {
      return new Promise((resolve, reject) => {
        this.$http.get('user/cookie')
          .then(response => {
            return resolve(response.logged_in);
          })
          .finally(() => {
            this.hidden = false;
            return reject();
          });
      });
    },

    openLogin() {
      this.view = LVIEW.DEFAULT;
      this.hidden = false;

      // also push same route without query params
      this.$router.push({ name: VIEW.LOGIN }).catch(() => { /** supress duplicate route warning */ });
    },

    redirect() {
      window.location.href = this.redirect_url;
    },

    handleElevatorMusic() {
      const volume = 0.2;
      const wait_before_redirect = 60000;
      this.$parent.music = this.$cdn + 'dist/assets/music/typical-elevator-music-and-on-hold_by_rightsfreemusic.mp3';
      let time_left = wait_before_redirect;

      const changeVolume = _ => {
        // make sure we keep the volume low
        if (this.$parent.$refs.music != null) {
          this.$parent.$refs.music.volume = volume;
        }
      };

      const updateText = () => {
        this.login_button_label = 'Playing elevator music. Redirecting in: ' + Math.round(time_left / 1000) + 'seconds';
      };

      updateText();

      const timer = setInterval(() => {
        time_left -= 1000;
        changeVolume();
        updateText();
      }, 1000);

      // set volume to low on next tick, when the music element should be mounted
      this.$nextTick()
        .then(_ => changeVolume() );

      return {wait_before_redirect, timer};
    },

    verifyEmail() {
      if (!this.email_verify_url) return;

      this.loading_verifying = true;

      this.$http.post(this.email_verify_url, { email: this.email })
        .then(_ => {
          this.email_verify_url = null;
        })
        .finally(_ => {
          this.loading_verifying = false;
        });
    }
  }
};
</script>
